import { TITLE_BAR_HEIGHT_PX } from '../chrome';

/** A box's size, in CSS px. */
export interface BoxSize {
    readonly widthPx: number;
    readonly heightPx: number;
}

/**
 * A widget's place and body size in its box, in CSS px. Placed from the right
 * and top edges, so it stays at the right as the box widens. A collapsed frame
 * keeps the size it reopens to.
 */
export interface WidgetFrame {
    readonly rightPx: number;
    readonly topPx: number;
    readonly widthPx: number;
    readonly heightPx: number;
    readonly collapsed: boolean;
}

/** The space kept between a widget and the edges of its box, in CSS px. */
export const WIDGET_INSET_PX = 8;
/** Opening top, in CSS px: clear of the tile title bars beneath. */
export const WIDGET_OPENING_TOP_PX = 36;
export const WIDGET_GAP_PX = 8;
/** The resize handle's square, in CSS px; body content keeps clear of it. */
export const WIDGET_RESIZE_HANDLE_PX = 14;
/** The smallest body a widget is drawn with, in CSS px. */
export const WIDGET_MIN_SIZE: BoxSize = { widthPx: 160, heightPx: 120 };

function clamp(value: number, low: number, high: number): number {
    return Math.min(Math.max(value, low), Math.max(low, high));
}

export function widgetOuterHeightPx(frame: WidgetFrame): number {
    return frame.collapsed
        ? TITLE_BAR_HEIGHT_PX
        : TITLE_BAR_HEIGHT_PX + frame.heightPx;
}

function narrowestWidthPx(minWidthPx: number): number {
    return Math.max(WIDGET_MIN_SIZE.widthPx, minWidthPx);
}

/**
 * The frame as drawn: inside the box less its insets, no narrower than
 * `minWidthPx`. A new frame, so a box that grows back shows the widget where
 * the user put it.
 */
export function fitWidgetFrame(
    frame: WidgetFrame,
    box: BoxSize,
    minWidthPx = 0
): WidgetFrame {
    const widthPx = clamp(
        frame.widthPx,
        narrowestWidthPx(minWidthPx),
        box.widthPx - 2 * WIDGET_INSET_PX
    );
    const heightPx = clamp(
        frame.heightPx,
        WIDGET_MIN_SIZE.heightPx,
        box.heightPx - 2 * WIDGET_INSET_PX - TITLE_BAR_HEIGHT_PX
    );
    const sized = { ...frame, widthPx, heightPx };
    return {
        ...sized,
        rightPx: clamp(
            frame.rightPx,
            WIDGET_INSET_PX,
            box.widthPx - WIDGET_INSET_PX - widthPx
        ),
        topPx: clamp(
            frame.topPx,
            WIDGET_INSET_PX,
            box.heightPx - WIDGET_INSET_PX - widgetOuterHeightPx(sized)
        ),
    };
}

/**
 * Opens left of `others` when it fits, else at the right edge. Not fitted, so a
 * box measured small during first layout cannot shrink the widget for good.
 */
export function openingWidgetFrame(
    size: BoxSize,
    box: BoxSize,
    others: ReadonlyArray<WidgetFrame>
): WidgetFrame {
    const pastOthersPx = others.reduce(
        (rightPx, other) =>
            Math.max(rightPx, other.rightPx + other.widthPx + WIDGET_GAP_PX),
        WIDGET_INSET_PX
    );
    const fitsPastOthers =
        pastOthersPx + size.widthPx + WIDGET_INSET_PX <= box.widthPx;
    return {
        rightPx: fitsPastOthers ? pastOthersPx : WIDGET_INSET_PX,
        topPx: WIDGET_OPENING_TOP_PX,
        widthPx: size.widthPx,
        heightPx: size.heightPx,
        collapsed: false,
    };
}

/** The frame moved by `dxPx` right and `dyPx` down, fitted to the box. */
export function movedWidgetFrame(
    frame: WidgetFrame,
    dxPx: number,
    dyPx: number,
    box: BoxSize,
    minWidthPx = 0
): WidgetFrame {
    return fitWidgetFrame(
        {
            ...frame,
            rightPx: frame.rightPx - dxPx,
            topPx: frame.topPx + dyPx,
        },
        box,
        minWidthPx
    );
}

/**
 * The frame resized from its bottom-right corner, fitted to the box. Holds the
 * left edge, or the right edge where the box leaves the left one no room.
 */
export function resizedWidgetFrame(
    frame: WidgetFrame,
    dxPx: number,
    dyPx: number,
    box: BoxSize,
    minWidthPx = 0
): WidgetFrame {
    const widthPx = clamp(
        frame.widthPx + dxPx,
        narrowestWidthPx(minWidthPx),
        box.widthPx - 2 * WIDGET_INSET_PX
    );
    return fitWidgetFrame(
        {
            ...frame,
            rightPx: frame.rightPx - (widthPx - frame.widthPx),
            widthPx,
            heightPx: frame.heightPx + dyPx,
        },
        box,
        minWidthPx
    );
}
